import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import html2canvas from 'html2canvas';
import SafeIcon from './common/SafeIcon';
import { FiActivity, FiZap, FiShield } from 'react-icons/fi';
import ThemeToggle from './components/ThemeToggle';
import useLocalStorage from './hooks/useLocalStorage';
import { calculateBMR, calculateMacros, generateProjections, ACTIVITY_MULTIPLIERS, GOAL_MODIFIERS } from './utils/calculations';
import InputForm from './components/InputForm';
import GoalSelector from './components/GoalSelector';
import MacroChart from './components/MacroChart';
import MacroBreakdown from './components/MacroBreakdown';
import Insights from './components/Insights';
import Education from './components/Education';
import ActionBar from './components/ActionBar';

// SEO & Content Components
import SEOIntro from './components/SEOIntro';
import Benefits from './components/Benefits';
import MaximizeGuide from './components/MaximizeGuide';
import Conclusion from './components/Conclusion';
import HealthTips from './components/HealthTips';

const App = () => {
  const [formData, setFormData] = useLocalStorage('tdee_lab_v2', {
    unit: 'metric', gender: 'male', age: 28, weight: 75, height: 180, activity: 'moderate', bodyFat: '',
  });

  const [results, setResults] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState('maintenance');
  const [isExporting, setIsExporting] = useState(false);
  const resultsRef = useRef(null);
  const exportAreaRef = useRef(null);

  const handleCalculate = (e) => {
    e.preventDefault();
    const bmr = calculateBMR(formData);
    const tdee = Math.round(bmr * ACTIVITY_MULTIPLIERS[formData.activity]);
    const goals = {};
    Object.keys(GOAL_MODIFIERS).forEach(k => goals[k] = Math.round(tdee * (1 + GOAL_MODIFIERS[k])));
    setResults({ bmr, tdee, goals });
    setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleDownloadJPG = async () => {
    if (!exportAreaRef.current) return;
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    try {
      const element = exportAreaRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: localStorage.getItem('theme') === 'dark' ? '#111827' : '#ffffff',
        onclone: (clonedDoc) => {
          const el = clonedDoc.querySelector('[data-export-root]');
          if (el) {
            el.style.width = '600px';
            el.style.padding = '40px';
          }
        }
      });
      const link = document.createElement('a');
      link.download = `TDEE-Blueprint-${new Date().toISOString().split('T')[0]}.jpg`;
      link.href = canvas.toDataURL('image/jpeg', 0.95);
      link.click();
    } catch (error) {
      alert('Export failed.');
    } finally {
      setIsExporting(false);
    }
  };

  const weightInLbs = formData.unit === 'imperial' ? parseFloat(formData.weight) : parseFloat(formData.weight) * 2.20462;
  const macros = results ? calculateMacros(results.goals[selectedGoal], selectedGoal, weightInLbs) : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-inter transition-colors">
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg">
              <SafeIcon icon={FiZap} className="w-5 h-5" />
            </div>
            <h1 className="text-xl font-black tracking-tighter uppercase italic">TDEE Lab</h1>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-16 pb-48">
        <SEOIntro />

        <section className="bg-white dark:bg-gray-800/50 rounded-3xl shadow-2xl p-6 md:p-8 mb-12 border border-gray-100 dark:border-gray-700">
          <InputForm formData={formData} setFormData={setFormData} onCalculate={handleCalculate} />
        </section>

        <AnimatePresence>
          {results && (
            <motion.div ref={resultsRef} initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} className="space-y-16">
              <div 
                ref={exportAreaRef} 
                data-export-root 
                className={`flex flex-col gap-12 ${isExporting ? 'bg-white dark:bg-gray-900 p-10 rounded-none' : ''}`}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-blue-600 rounded-[2rem] p-10 text-white shadow-2xl relative overflow-hidden">
                    <span className="text-[10px] font-black uppercase tracking-widest opacity-60">Daily Expenditure (TDEE)</span>
                    <div className="text-7xl font-black my-4 tracking-tighter">{results.tdee.toLocaleString()}</div>
                    <SafeIcon icon={FiActivity} className="absolute -bottom-4 -right-4 w-40 h-40 opacity-10" />
                  </div>
                  <div className="bg-white dark:bg-gray-800 rounded-[2rem] p-10 border border-gray-200 dark:border-gray-700 shadow-xl">
                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Resting Metabolic Rate</span>
                    <div className="text-7xl font-black my-4 tracking-tighter">{results.bmr.toLocaleString()}</div>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-[2.5rem] p-8 md:p-12 shadow-xl border border-gray-100 dark:border-gray-700">
                  <GoalSelector goals={results.goals} selected={selectedGoal} onSelect={setSelectedGoal} />
                  <div className="mb-12 pb-8 border-b-2 border-gray-50 dark:border-gray-800">
                    <h2 className="text-6xl font-black tracking-tighter text-blue-600 mb-2">{results.goals[selectedGoal]} <span className="text-xl font-medium text-gray-400">kcal</span></h2>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <MacroChart macros={macros} theme={localStorage.getItem('theme') || 'dark'} />
                    <div className="space-y-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                        Based on your goal of <strong>{selectedGoal.replace('_', ' ')}</strong>, we suggest the following macronutrient split to optimize body composition and hormonal health.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 text-[10px] font-bold uppercase rounded-full">High Protein</span>
                        <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 text-[10px] font-bold uppercase rounded-full">Moderate Carbs</span>
                      </div>
                    </div>
                  </div>

                  <MacroBreakdown macros={macros} />
                </div>
              </div>

              <div className="space-y-20">
                <Benefits />
                <Insights tdee={results.tdee} goalCalories={results.goals[selectedGoal]} goalType={selectedGoal} activity={formData.activity} weightLbs={weightInLbs} />
                <MaximizeGuide />
                <Education />
                <HealthTips />
                <Conclusion />
              </div>

              <ActionBar onDownload={handleDownloadJPG} shareData={{ tdee: results.tdee, goalName: selectedGoal, goalCalories: results.goals[selectedGoal] }} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 py-12 px-4 text-center">
        <div className="max-w-4xl mx-auto flex flex-col gap-4 items-center">
          <div className="flex items-center gap-2 text-green-600">
            <SafeIcon icon={FiShield} className="w-4 h-4" />
            <span className="text-[10px] font-black uppercase tracking-widest">SSL Encrypted • Private Processing</span>
          </div>
          <div className="text-[10px] font-bold text-gray-400 tracking-widest uppercase">
            © 2026 TDEE Intelligence Lab • A SHEKER.COM PROJECT
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;